<script setup>
import {getCurrentInstance, onMounted, onUnmounted, ref, watch, computed} from 'vue'
import {useI18n} from 'vue-i18n'
import Swal from 'sweetalert2'

import $http from '@/axios.config'
import store from '@/store'

import LogsTable from '@/components/admin-page/LogsTable.vue'
import ClientListItem from '@/components/items/ClientListItem.vue'
import UserListItem from '@/components/items/UserListItem.vue'
import AccessRequestListItem from '@/components/items/AccessRequestListItem.vue'
import ClientForm from '@/components/forms/ClientForm.vue'
import {VueAwesomePaginate} from 'vue-awesome-paginate'
import AllOrdersTable from '@/components/parts/AllOrdersTable.vue'
import StatusManager from '@/components/admin-page/StatusManager.vue';

import 'vue-awesome-paginate/dist/style.css'
import {log} from "@/utils/clientLogger.js";
import OrderBadgeIndex from "@/components/resources/order-badges/OrderBadgeIndex.vue";

const {t} = useI18n({useScope: 'global'})

const isAdmin = computed(() => store.getters.isAdmin)
const userRole = computed(() => store.getters.userData?.role?.name)
const isDistributor = computed(() => userRole.value === 'distributor')
const userLogin = computed(() => store.getters.userData?.login || store.getters.userData?.username);

const users = ref([])
const clients = ref([])
const requests = ref([])
const roles = ref([])
const logs = ref([])

const activeUser = ref({role: ''})
const activeClient = ref({})
const activeClientId = ref(null)

const closeUserModalBtn = ref(null)
const closeClientModalBtn = ref(null)

const instance = getCurrentInstance()

const clientsPagination = ref({page: 1, perPage: 20, totalItems: 0, name: ''})
const usersPagination = ref({page: 1, perPage: 20, totalItems: 0, name: ''})
const logsPagination = ref({page: 1, perPage: 20, totalItems: 0, name: ''})
const requestsPagination = ref({page: 1, perPage: 20, totalItems: 0})

function editUser(userId) {
  const user = users.value.find(u => u._id === userId)
  activeUser.value = user
      ? {id: user._id, username: user.username, role: user.role._id, password: '', isActive: user.isActive}
      : {role: ''}
}

function editClient(clientId) {
  const client = clients.value.find(c => c._id === clientId)
  activeClient.value = client || {}
}

function updateUser() {
  const user = activeUser.value
  const request = user.id ? $http.put(`/api/users/${user.id}`, user) : $http.post('/api/users', user)

  request.then(response => {
    showUsers()
    closeUserModalBtn.value?.click()
    Swal.fire({title: t('alerts.success'), text: response.data?.message, icon: 'success'})
  }).catch(e => {
    Swal.fire({title: t('alerts.error'), text: e.response?.data?.error?.message || t('alerts.error'), icon: 'error'})
  })
}

function handleClientSuccess() {
  showClients();
  closeClientModalBtn.value?.click();
}

function getLogs() {
  $http.get('/api/logs', {params: {limit: logsPagination.value.perPage, page: logsPagination.value.page}}).then(res => {
    logs.value = res.data.data
    logsPagination.value.totalItems = res.data.total
  }).catch(error => {
    log.error('View:AdminPage:getLogs', {'error': error})
  })
}

function showUsers() {
  $http.get('/api/users', {params: {limit: usersPagination.value.perPage, page: usersPagination.value.page, name: usersPagination.value.name}}).then(res => {
    users.value = res.data.data
    usersPagination.value.totalItems = res.data.total
  }).catch(error => {
    log.error('View:AdminPage:showUsers', {'error': error})
  })
}

function showClients() {
  if (clientsPagination.value.name === userLogin.value) {
    clientsPagination.value.name = "";
  }

  $http.get('/api/clients', {params: {limit: clientsPagination.value.perPage, page: clientsPagination.value.page, name: clientsPagination.value.name}}).then(res => {
    clients.value = res.data.clientData
    clientsPagination.value.totalItems = res.data.clientsCount
  }).catch(error => {
    log.error('View:AdminPage:showClients', {'error': error})
  })
}

function getRequests() {
  $http.get('/api/clients/access-requests', {params: {limit: requestsPagination.value.perPage, page: requestsPagination.value.page}})
      .then(res => {
        requests.value = res.data.data
        requestsPagination.value.totalItems = res.data.total
      })
      .catch(error => {
        log.error('View:AdminPage:getRequests', {'error': error})
      })
}

function handleRequest(id, status) {
  $http.put(`/api/clients/access-requests/${id}`, { status })
      .then(() => {
        Swal.fire({
          title: t('alerts.success'),
          text: status === 'approved' ? t('messages.accessGranted') : t('messages.accessRejected'),
          icon: 'success',
          timer: 1500
        });
        getRequests();
        if (status === 'approved') {
          showClients();
        }
      })
      .catch(error => {
        Swal.fire({title: t('alerts.error'), text: error.response?.data?.error || 'Error', icon: 'error'})
      })
}

onMounted(() => {
  const socket = instance.appContext.config.globalProperties.$socket

  document.getElementById('editUserModal')?.addEventListener('show.bs.modal', e => {
    const id = e.relatedTarget?.getAttribute('data-bs-user')
    editUser(id)
  })
  document.getElementById('editClientModal')?.addEventListener('show.bs.modal', e => {
    const id = e.relatedTarget?.getAttribute('data-bs-client')
    editClient(id)
  })
  document.getElementById('clientOrdersModal')?.addEventListener('show.bs.modal', e => {
    activeClientId.value = e.relatedTarget?.getAttribute('data-bs-client')
    activeClient.value = clients.value.find(c => c._id === activeClientId.value)
  })

  $http.get('/api/users/roles').then(res => {
    roles.value = res.data
  }).catch(error => {
    log.error('View:AdminPage:onMounted', {'error': error})
  })

  socket.on('new-log', getLogs)
  socket.on('update-client', showClients)
  socket.on('update-users', showUsers)

  socket.on('new-info', () => {
    if (isAdmin.value) {
      getRequests();
    }
  });

  if (isAdmin.value) {
    getLogs()
    getRequests()
  } else {
    if (isDistributor.value) {
      clientsPagination.value.name = '';
    }
    showClients()
  }
})

onUnmounted(() => {
  const socket = instance.appContext.config.globalProperties.$socket
  socket.off('new-log', getLogs)
  socket.off('update-client', showClients)
  socket.off('update-users', showUsers)
  socket.off('new-info')
});

watch(
    [() => logsPagination.value.page, () => usersPagination.value.page, () => clientsPagination.value.page, () => requestsPagination.value.page],
    ([newLogsPage, newUsersPage, newClientsPage, newRequestsPage], [oldLogsPage, oldUsersPage, oldClientsPage, oldRequestsPage]) => {
      if (newLogsPage !== oldLogsPage) getLogs();
      if (newUsersPage !== oldUsersPage) showUsers();
      if (newClientsPage !== oldClientsPage) showClients();
      if (newRequestsPage !== oldRequestsPage) getRequests();
    }
);
</script>

<template>
  <div class="container mt-5">
    <ul class="nav nav-tabs" id="myTab" role="tablist">
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button class="nav-link active"
                id="history-tab"
                data-bs-toggle="tab"
                data-bs-target="#history"
                type="button"
                role="tab"
                aria-controls="history"
                aria-selected="true"
                @click="getLogs"
        >
          {{ t('tableheadings.history') }}
        </button>
      </li>

      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button class="nav-link"
                id="requests-tab"
                data-bs-toggle="tab"
                data-bs-target="#requests"
                type="button"
                role="tab"
                aria-controls="requests"
                aria-selected="false"
                @click="getRequests"
        >
          {{ t('tableheadings.requests') || 'Запити' }}
          <span v-if="requestsPagination.totalItems > 0" class="badge bg-danger rounded-pill ms-1">{{ requestsPagination.totalItems }}</span>
        </button>
      </li>

      <li class="nav-item" role="presentation">
        <button :class="{'nav-link': true, 'active': !isAdmin && !isDistributor}"
                id="clients-tab"
                data-bs-toggle="tab"
                data-bs-target="#clients" type="button" role="tab"
                aria-controls="clients"
                aria-selected="false"
                @click="showClients"
        >
          {{ t('tableheadings.clients') }}
        </button>
      </li>
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button class="nav-link"
                id="users-tab"
                data-bs-toggle="tab"
                data-bs-target="#users"
                type="button"
                role="tab"
                aria-controls="contact"
                aria-selected="false"
                @click="showUsers"
        >
          {{ t('tableheadings.users') }}
        </button>
      </li>
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button class="nav-link"
                id="statuses-tab"
                data-bs-toggle="tab"
                data-bs-target="#statuses"
                type="button"
                role="tab"
                aria-controls="statuses"
                aria-selected="false"
        >
          {{ t('tableheadings.statuses') }}
        </button>
      </li>
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button class="nav-link"
                id="badges-tab"
                data-bs-toggle="tab"
                data-bs-target="#badges"
                type="button"
                role="tab"
                aria-controls="badges"
                aria-selected="false"
        >
          {{ t('tableheadings.badges') }}
        </button>
      </li>
    </ul>

    <div class="tab-content" id="myTabContent">
      <div v-if="isAdmin" :class="{'tab-pane fade': true, 'show active': isAdmin}" id="history" role="tabpanel" aria-labelledby="history-tab">
        <LogsTable :logs="logs"/>
        <div class="paginate mt-3 d-flex justify-content-center">
          <vue-awesome-paginate
              :total-items="logsPagination.totalItems"
              :items-per-page="logsPagination.perPage"
              :max-pages-shown="15"
              v-model="logsPagination.page"
          />
        </div>

      </div>

      <div v-if="isAdmin" class="tab-pane fade" id="requests" role="tabpanel" aria-labelledby="requests-tab">
        <div v-if="requests.length === 0" class="alert alert-info mt-3 text-center">
          {{ t('messages.noRequests') || 'Немає активних запитів' }}
        </div>

        <ol class="list-group list-group-numbered my-4" v-else>
          <AccessRequestListItem
              v-for="req in requests"
              :key="req._id"
              :request="req"
              @approve="handleRequest($event, 'approved')"
              @reject="handleRequest($event, 'rejected')"
          />
        </ol>

        <div class="paginate mt-3 d-flex justify-content-center" v-if="requests.length > 0">
          <vue-awesome-paginate
              :total-items="requestsPagination.totalItems"
              :items-per-page="requestsPagination.perPage"
              :max-pages-shown="5"
              v-model="requestsPagination.page"
          />
        </div>
      </div>

      <div v-if="isAdmin" class="tab-pane fade show" id="users" role="tabpanel" aria-labelledby="users-tab">
        <div class="input-group mt-3">
          <input type="text" class="form-control" v-model="usersPagination.name" @input="showUsers" :placeholder="t('placeholders.searchUserByName')">
        </div>

        <ol class="list-group list-group-numbered my-4">
          <UserListItem
              v-for="(user,index) in users"
              :key="index"
              :user="user"
          />
        </ol>

        <button class="btn btn-primary w-100" data-bs-target="#editUserModal" data-bs-toggle="modal">
          + {{ t('buttons.addUser') }}
        </button>

        <div class="paginate mt-3 d-flex justify-content-center">
          <vue-awesome-paginate
              :total-items="usersPagination.totalItems"
              :items-per-page="usersPagination.perPage"
              :max-pages-shown="5"
              v-model="usersPagination.page"
          />
        </div>
      </div>
      <div :class="{'tab-pane fade': true, 'show active': !isAdmin}" id="clients" role="tabpanel" aria-labelledby="clients-tab">

        <div class="input-group mt-3">
          <input type="text" class="form-control"
                 v-model="clientsPagination.name"
                 @input="showClients"
                 readonly
                 @focus="($event) => $event.target.removeAttribute('readonly')"
                 :placeholder="t('placeholders.searchClientByName')">
        </div>


        <ol class="list-group list-group-numbered my-4">
          <ClientListItem
              v-for="(client,index) in clients"
              :key="index"
              :client="client"
          />
        </ol>

        <button class="btn btn-primary w-100 my-2" data-bs-target="#editClientModal" data-bs-toggle="modal">
          + {{ t('buttons.addClient') }}
        </button>

        <div class="paginate mt-3 d-flex justify-content-center">
          <vue-awesome-paginate
              :total-items="clientsPagination.totalItems"
              :items-per-page="clientsPagination.perPage"
              :max-pages-shown="5"
              v-model="clientsPagination.page"
          />
        </div>

      </div>
      <div v-if="isAdmin"
           class="tab-pane fade"
           id="statuses"
           role="tabpanel"
           aria-labelledby="statuses-tab">
        <StatusManager/>
      </div>
      <div v-if="isAdmin"
           class="tab-pane fade"
           id="badges"
           role="tabpanel"
           aria-labelledby="badges-tab">
        <OrderBadgeIndex/>
      </div>
    </div>

    <div class="modal fade" id="editUserModal" data-bs-backdrop="true" data-bs-keyboard="false" tabindex="-1" aria-labelledby="editUserModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editUserModalLabel">
              <template v-if="activeUser.username">{{ t('buttons.edit') }} {{ activeUser.username }}</template>
              <template v-else>{{ t('buttons.addUser') }}</template>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" ref="closeUserModalBtn" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <input type="text" class="form-control" :placeholder="t('placeholders.userLogin')" v-model="activeUser.username">
            </div>
            <div class="form-group mt-3">
              <input type="password" class="form-control" :placeholder="t('placeholders.userPassword')" v-model="activeUser.password">
            </div>
            <div class="form-group mt-3">
              <label class="pb-1" for="userGroup">{{ t('labels.userGroup') }}</label>
              <select class="form-control" v-model="activeUser.role" id="userGroup">
                <option v-for="role in roles" :key="role._id" :value="role._id">{{ role.name }}</option>
              </select>
            </div>
            <div class="form-check mt-2">
              <input class="form-check-input" type="checkbox" v-model="activeUser.isActive" id="isActiveUser">
              <label class="form-check-label" for="isActiveUser">{{ t('labels.isActiveUser') }}</label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" @click="updateUser" class="btn btn-primary">{{ t('buttons.confirm') }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="editClientModal" data-bs-backdrop="true" data-bs-keyboard="false" tabindex="-1" aria-labelledby="editClientModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editClientModalLabel">
              <template v-if="activeClient.name">{{ t('buttons.edit') }} {{ activeClient.name }}</template>
              <template v-else>{{ t('buttons.addClient') }}</template>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" ref="closeClientModalBtn" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <ClientForm v-model="activeClient" @submit-client="handleClientSuccess"/>
          </div>
          <div class="modal-footer"></div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="clientOrdersModal" data-bs-backdrop="true" data-bs-keyboard="false" tabindex="-1" aria-labelledby="clientOrdersModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="clientOrdersModalLabel">
              <template v-if="activeClient.name">{{ t('headings.clientOrder') }} {{ activeClient.name }}</template>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <AllOrdersTable v-model="activeClientId"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.pagination-container {
  display: flex;
  column-gap: 10px;
}

.paginate-buttons {
  height: 40px;
  width: 40px;
  border-radius: 20px;
  cursor: pointer;
  background-color: var(--bs-gray-300);
  border: 1px solid var(--bs-gray-300);
  color: black;

  &:hover {
    opacity: .80;
  }
}

.active-page {
  background-color: var(--bs-primary);
  border: 1px solid;
  color: white;

  &:hover {
    opacity: .80;
    background-color: var(--bs-primary);
  }
}
</style>